import React from 'react';
import { useSelector } from 'react-redux';
import { ModalHeader } from '../../../../CommonComponents/OPL/Modal/ModalHeader';
import { ModalCloseButton } from '../../../../CommonComponents/OPL/Modal/ModalCloseButton';
import { ModalFooter } from '../../../../CommonComponents/OPL/Modal/ModalFooter';
import { Button } from '../../../../CommonComponents/OPL/Button/Button';
import { Modal } from '../../../../CommonComponents/OPL/Modal/Modal';
import { Box } from '../../../../CommonComponents/OPL/Box/Box';
import { Tab, TabList, TabPanel, Tabs } from '../../../../CommonComponents/OPL/Tab';
import { Text } from '../../../../CommonComponents/OPL/Text/Text';
import { selectExternalAccountProviderAccess } from '../../../../CommonSelectors/CommonAreaSelector';
import { AkoyaAccounts } from './components/AkoyaAccounts/AkoyaAccounts';
import { ErrorState } from '../../../../CommonComponents/OPL/AsyncStates/ErrorState/ErrorState';
import { AkoyaAccountsLoading } from './components/AkoyaAccounts/AkoyaAccounts.loading';
import { useManagedAkoyaLinkedAccounts } from './useManagedAkoyaLinkedAccounts';

interface ManageLinkedAccountsProps {
  onClose(): void;
  onBack(): void;
  onExternalAccountLinked?: () => void;
}

export const ManageAkoyaLinkedAccounts = ({
  onClose,
  onBack,
  onExternalAccountLinked = () => {},
}: ManageLinkedAccountsProps) => {
  // Keep the query mounted to prevent re-fetches when switching tabs
  useManagedAkoyaLinkedAccounts();

  const externalAccountProviders = useSelector(selectExternalAccountProviderAccess);

  return (
    <Modal isOpen onRequestClose={onClose}>
      <ModalHeader>Manage Linked Accounts</ModalHeader>
      <ModalCloseButton onClick={onClose} />

      <Tabs css="overflow-x: auto">
        <TabList px="24">
          {externalAccountProviders.map(provider => {
            return <Tab key={provider.id}>{provider.name}</Tab>;
          })}
        </TabList>

        {externalAccountProviders.map(provider => {
          return (
            <TabPanel key={provider.id}>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mx="24"
                py="16"
              >
                <Box display="flex" alignItems="center">
                  {provider.imageUrl && (
                    <img
                      src={provider.imageUrl}
                      alt={provider.name}
                      css="width: 32px;
                        height: 32px;
                        object-fit: contain;
                        box-sizing: border-box;"
                    />
                  )}

                  <Text pl="4" fontWeight={600} fontSize="h6">
                    {provider.name}
                  </Text>
                </Box>

                <Button
                  leftIcon="fas fa-plus"
                  variant="link"
                  onClick={() => {
                    window.open(
                      `${provider.url}${
                        ADV.settings.tokens.access_token
                          ? `&access_token=${ADV.settings.tokens.access_token}`
                          : ''
                      }`,
                      '_blank',
                    );

                    onExternalAccountLinked();
                  }}
                >
                  Add New Account
                </Button>
              </Box>

              <Box as="form" mb="24" minHeight="400px">
                <ManageAkoyaLinkedAccountsContent providerId={provider.id} />
              </Box>
            </TabPanel>
          );
        })}
      </Tabs>

      <ModalFooter>
        <Box flex={1}>
          <Button leftIcon="fas fa-chevron-left" variant="secondary" onClick={onBack}>
            Back
          </Button>
        </Box>

        <Button variant="subtleInactive" onClick={onClose} mr="8">
          Cancel
        </Button>
      </ModalFooter>
    </Modal>
  );
};

interface ContentProps {
  providerId: string;
}

const ManageAkoyaLinkedAccountsContent = ({ providerId }: ContentProps) => {
  const { isLoading, isError, refetch, data } = useManagedAkoyaLinkedAccounts();

  if (isLoading) {
    return <AkoyaAccountsLoading />;
  }

  if (isError) {
    return <ErrorState onAction={refetch} />;
  }

  if (!data) {
    throw new Error('No data for Akoya accounts');
  }

  return <AkoyaAccounts providerId={providerId} accounts={data[providerId] || []} />;
};
